import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import type { CertificateCheckoutPaymentMethod } from "@/services/certificatePaymentsService";

export interface PaymentMethodSelectorProps {
  value: CertificateCheckoutPaymentMethod;
  onChange: (value: CertificateCheckoutPaymentMethod) => void;
  disabled?: boolean;
  className?: string;
}

const OPTIONS: ReadonlyArray<{
  value: CertificateCheckoutPaymentMethod;
  labelKey: string;
  hintKey: string;
}> = [
  {
    value: "dlocal",
    labelKey: "requests.user.bankTransfer.methodCard",
    hintKey: "requests.user.bankTransfer.methodCardHint",
  },
  {
    value: "bank_transfer",
    labelKey: "requests.user.bankTransfer.methodTransfer",
    hintKey: "requests.user.bankTransfer.methodTransferHint",
  },
];

/**
 * Card vs bank transfer, chosen before the checkout is created.
 *
 * The choice has to happen HERE rather than inside the payment provider's
 * hosted page, which is the whole point: dLocal's own method picker is where
 * Uruguayan bank transfer breaks (DP-8347268 died on their error page, and no
 * transfer has ever completed through them).
 *
 * Radios rather than a segmented control or a dropdown: two options with a
 * line of explanation each, where the difference between them is what the
 * consumer has to DO next, not a preference. Native inputs keep keyboard and
 * screen-reader behaviour without reimplementing it.
 */
export function PaymentMethodSelector({
  value,
  onChange,
  disabled,
  className,
}: PaymentMethodSelectorProps) {
  const { t } = useTranslation();

  return (
    <fieldset className={cn("mt-6", className)} disabled={disabled}>
      <legend className="mb-2 text-sm font-medium text-gray-900">
        {t("requests.user.bankTransfer.methodTitle")}
      </legend>
      <div className="space-y-2">
        {OPTIONS.map((option) => {
          const selected = value === option.value;
          return (
            <label
              key={option.value}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors",
                selected ? "border-blue-500 bg-blue-50/60" : "border-gray-200 hover:border-gray-300",
                disabled && "cursor-not-allowed opacity-60",
              )}
            >
              <input
                type="radio"
                name="certificate-payment-method"
                value={option.value}
                checked={selected}
                onChange={() => onChange(option.value)}
                className="mt-0.5 h-4 w-4 text-blue-600"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">{t(option.labelKey)}</span>
                <span className="block text-xs text-gray-500">{t(option.hintKey)}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
